"use client";

import * as React from "react";
import { Collection } from "react-aria-components/Collection";
import type { Selection } from "react-aria-components";
import {
  AlertDialog,
  Button,
  cn,
  Dropdown,
  Label,
  SearchField,
  toast,
} from "@heroui/react";
import { FileTree, useFileTree } from "@heroui-pro/react";
import {
  IconTrees as TreeDeciduous,
  IconUser as User,
  IconPlus as Plus,
  IconDotsVertical as MoreVertical,
  IconFolderPlus as FolderPlus,
  IconPencil as Edit,
  IconTrash as Trash2,
  IconCrown as Crown,
} from "@tabler/icons-react";
import { useAppSelector } from "@hs/shared/stores/hooks";
import { buildTree } from "@hs/shared";
import { trpc } from "@/lib/trpc";
import type { ContactsOrgNode } from "../types";
import { SPACE_ROOT_ID } from "../types";
import { OrgEditorDialog, OrgEditorMode } from "./OrgEditorDialog";
import { PeopleEditorDialog } from "./PeopleEditorDialog";
import { PeopleSelectDialog } from "@/components/PeopleSelectDialog";
import type { TreeSelectNode } from "@/components/custom";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export { SPACE_ROOT_ID } from "../types";

interface SidebarTreeItem extends TreeSelectNode {
  rawOrg?: Record<string, unknown> | null;
  isSpaceRoot?: boolean;
}

function findInTree(nodes: SidebarTreeItem[], id: string): SidebarTreeItem | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const hit = findInTree(n.children as SidebarTreeItem[], id);
      if (hit) return hit;
    }
  }
  return undefined;
}

/** 仅在空间根下的组织子树内过滤，始终保留根节点（与旧行为一致） */
function filterSidebarTree(rootList: SidebarTreeItem[], q: string): SidebarTreeItem[] {
  if (!q.trim()) return rootList;
  const root = rootList[0];
  if (!root) return [];
  const lower = q.toLowerCase();
  const walk = (list: SidebarTreeItem[]): SidebarTreeItem[] => {
    const out: SidebarTreeItem[] = [];
    for (const node of list) {
      const matchName = node.label.toLowerCase().includes(lower);
      const kids = node.children ? walk(node.children as SidebarTreeItem[]) : [];
      if (matchName || kids.length) {
        out.push({
          ...node,
          children: kids.length ? kids : undefined,
        });
      }
    }
    return out;
  };
  const filteredChildren = root.children ? walk(root.children as SidebarTreeItem[]) : [];
  return [{ ...root, children: filteredChildren.length > 0 ? filteredChildren : undefined }];
}

function OrgRowActions({
  org,
  onAddChild,
  onSetLeader,
  onEdit,
  onDelete,
}: {
  org: Record<string, unknown>;
  onAddChild: (o: Record<string, unknown>) => void;
  onSetLeader: (o: Record<string, unknown>) => void;
  onEdit: (o: Record<string, unknown>) => void;
  onDelete: (o: Record<string, unknown>) => void;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div
      className={cn(
        "absolute end-0 top-1/2 z-10 flex -translate-y-1/2 items-center pe-0.5",
        "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
        menuOpen && "opacity-100"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown isOpen={menuOpen} onOpenChange={setMenuOpen}>
        <Button
          isIconOnly
          aria-label="操作"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground !h-6 !min-h-6 !w-6 !min-w-6 !p-0"
        >
          <MoreVertical className="size-3.5" stroke={1.5} />
        </Button>
        <Dropdown.Popover className="min-w-[160px]" placement="bottom end">
          <Dropdown.Menu>
            <Dropdown.Item id="add-child" textValue="添加子组织" onAction={() => onAddChild(org)}>
              <Label className="flex items-center gap-2">
               <FolderPlus className="size-4" stroke={1.5} />
                添加子组织
              </Label>
            </Dropdown.Item>
            <Dropdown.Item id="leader" textValue="设置主管" onAction={() => onSetLeader(org)}>
              <Label className="flex items-center gap-2">
                <Crown className="size-4" stroke={1.5} />
                设置主管
              </Label>
            </Dropdown.Item>
            <Dropdown.Item id="edit" textValue="编辑" onAction={() => onEdit(org)}>
              <Label className="flex items-center gap-2">
                <Edit className="size-4" stroke={1.5} />
                编辑
              </Label>
            </Dropdown.Item>
            <Dropdown.Item
              id="delete"
              textValue="删除"
              variant="danger"
              onAction={() => onDelete(org)}
            >
              <Label className="flex items-center gap-2">
                <Trash2 className="size-4" stroke={1.5} />
                删除
              </Label>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
}

function renderSidebarItem(
  item: SidebarTreeItem,
  ctx: {
    onAddChild: (o: Record<string, unknown>) => void;
    onSetLeader: (o: Record<string, unknown>) => void;
    onEdit: (o: Record<string, unknown>) => void;
    onDelete: (o: Record<string, unknown>) => void;
  }
) {
  const hasKids = !!(item.children && item.children.length > 0);
  const titleNode = (
    <span className="relative flex min-w-0 flex-1 items-center pe-7">
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {!item.isSpaceRoot && item.rawOrg ? (
        <OrgRowActions
          org={item.rawOrg as Record<string, unknown>}
          onAddChild={ctx.onAddChild}
          onSetLeader={ctx.onSetLeader}
          onEdit={ctx.onEdit}
          onDelete={ctx.onDelete}
        />
      ) : null}
    </span>
  );

  return (
    <FileTree.Item
      className="group"
      dragIcon={false}
      id={item.id}
      textValue={item.label}
      title={titleNode}
    >
      {hasKids ? (
        <Collection items={item.children as SidebarTreeItem[]}>
          {(child) => renderSidebarItem(child as SidebarTreeItem, ctx)}
        </Collection>
      ) : null}
    </FileTree.Item>
  );
}

interface ContactsSidebarProps {
  onSelectChange?: (item: ContactsOrgNode | undefined) => void;
  selectedOrg?: ContactsOrgNode | undefined;
}

export function ContactsSidebar({ onSelectChange, selectedOrg }: ContactsSidebarProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const [isOrgDialogOpen, setIsOrgDialogOpen] = React.useState(false);
  const [isPeopleDialogOpen, setIsPeopleDialogOpen] = React.useState(false);
  const [orgDialogMode, setOrgDialogMode] = React.useState<OrgEditorMode>("create");
  const [editingOrg, setEditingOrg] = React.useState<Record<string, unknown> | null>(null);
  const [parentOrg, setParentOrg] = React.useState<Record<string, unknown> | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [orgToDelete, setOrgToDelete] = React.useState<Record<string, unknown> | null>(null);
  const [isSetLeaderDialogOpen, setIsSetLeaderDialogOpen] = React.useState(false);
  const [orgForSetLeader, setOrgForSetLeader] = React.useState<Record<string, unknown> | null>(null);

  const user = useAppSelector((state) => state.auth.user);
  const spaceName = user?.spaces?.[0]?.name ?? "空间";
  const utils = trpc.useUtils();

  const { data: orgTreeData, isLoading, refetch } = trpc.contacts.findAllSpaceTrees.useQuery(undefined, {
    staleTime: 0,
  });

  const handleAddChild = React.useCallback((org: Record<string, unknown>) => {
    setOrgDialogMode("add-child");
    setParentOrg(org);
    setEditingOrg(null);
    setIsOrgDialogOpen(true);
  }, []);

  const handleEdit = React.useCallback((org: Record<string, unknown>) => {
    setOrgDialogMode("edit");
    setEditingOrg(org);
    setParentOrg(null);
    setIsOrgDialogOpen(true);
  }, []);

  const handleDelete = React.useCallback((org: Record<string, unknown>) => {
    setOrgToDelete(org);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleSetLeader = React.useCallback((org: Record<string, unknown>) => {
    setOrgForSetLeader(org);
    setIsSetLeaderDialogOpen(true);
  }, []);

  const setOrgLeaderMutation = trpc.contacts.updateOrg.useMutation({
    onSuccess: () => {
      toast.success("设置主管成功");
      setIsSetLeaderDialogOpen(false);
      setOrgForSetLeader(null);
      void refetch();
      void utils.contacts.findAllSpaceTrees.invalidate();
    },
  });

  const deleteOrgMutation = trpc.contacts.deleteOrg.useMutation({
    onSuccess: () => {
      toast.success("组织删除成功");
      setIsDeleteDialogOpen(false);
      setOrgToDelete(null);
      void refetch();
      void utils.contacts.findAllSpaceTrees.invalidate();
    },
  });

  const handleConfirmDelete = React.useCallback(() => {
    const id = orgToDelete?.id as string | undefined;
    if (id) deleteOrgMutation.mutate({ id });
  }, [orgToDelete, deleteOrgMutation]);

  const handleSetLeaderConfirm = React.useCallback(
    (
      selected: {
        peopleId: string;
        fullName?: string | null;
        jobNumber?: string | null;
        avatarUrl?: string | null;
      }[]
    ) => {
      const oid = orgForSetLeader?.id as string | undefined;
      if (!oid || selected.length === 0) return;
      setOrgLeaderMutation.mutate({
        id: oid,
        data: { leaderId: selected[0].peopleId },
      });
    },
    [orgForSetLeader, setOrgLeaderMutation]
  );

  const treeCtx = React.useMemo(
    () => ({
      onAddChild: handleAddChild,
      onSetLeader: handleSetLeader,
      onEdit: handleEdit,
      onDelete: handleDelete,
    }),
    [handleAddChild, handleSetLeader, handleEdit, handleDelete]
  );

  const fullTree: SidebarTreeItem[] = React.useMemo(() => {
    const convert = (orgs: Record<string, unknown>[]): SidebarTreeItem[] =>
      orgs.map((org) => ({
        id: String(org.id),
        label: String(org.shortName || org.fullName || ""),
        rawOrg: org,
        children: org.children ? convert(org.children as Record<string, unknown>[]) : undefined,
      }));

    const orgChildren: SidebarTreeItem[] =
      orgTreeData && orgTreeData.length > 0
        ? (() => {
            const valid = orgTreeData.filter((o) => o.id != null);
            const tree = buildTree(
              valid.map((org) => ({
                id: org.id!,
                parentId: org.parentId || null,
                shortName: org.shortName,
                fullName: org.fullName,
                code: org.code,
                type: org.type,
                order: org.order || 0,
              })),
              { parentField: "parentId", sortBy: "order", sortOrder: "asc" }
            );
            return convert(tree as Record<string, unknown>[]);
          })()
        : [];

    const root: SidebarTreeItem = {
      id: SPACE_ROOT_ID,
      label: spaceName,
      isSpaceRoot: true,
      rawOrg: null,
      children: orgChildren.length > 0 ? orgChildren : undefined,
    };
    return [root];
  }, [orgTreeData, spaceName]);

  const filteredTree = React.useMemo(
    () => filterSidebarTree(fullTree, searchValue),
    [fullTree, searchValue]
  );

  const { expandableKeys } = useFileTree({ items: filteredTree });

  const hasInitialized = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitialized.current && !selectedOrg && fullTree.length > 0 && onSelectChange) {
      const first = fullTree[0];
      onSelectChange({
        id: first.id,
        name: first.label,
        isSpaceRoot: first.isSpaceRoot,
        rawOrg: first.rawOrg ?? null,
      });
      hasInitialized.current = true;
    }
  }, [fullTree, selectedOrg, onSelectChange]);

  const selectedKeys = React.useMemo<Selection>(
    () => (selectedOrg?.id ? new Set([selectedOrg.id]) : new Set()),
    [selectedOrg?.id]
  );

  const handleTreeSelection = React.useCallback(
    (keys: Selection) => {
      if (keys === "all" || keys.size === 0) {
        onSelectChange?.(undefined);
        return;
      }
      const id = [...keys][0] as string;
      const node = findInTree(fullTree, id);
      if (!node) return;
      onSelectChange?.({
        id: node.id,
        name: node.label,
        rawOrg: node.rawOrg ?? null,
        isSpaceRoot: node.isSpaceRoot === true,
      });
    },
    [fullTree, onSelectChange]
  );

  const handleOrgCreateSuccess = () => {
    void refetch();
    void utils.contacts.findAllSpaceTrees.invalidate();
  };

  return (
    <>
      <div className="bg-sidebar flex h-full w-64 shrink-0 flex-col border-r border-[var(--border)]">
        <div className="flex h-14 shrink-0 items-center border-b border-[var(--border)] px-4">
          <h2 className="text-xl font-bold">组织架构</h2>
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-b border-[var(--border)] px-3 py-3">
          <div className="flex items-center gap-2">
            <SearchField
              aria-label="搜索组织"
              className="min-w-0 flex-1"
              value={searchValue}
              onChange={setSearchValue}
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input className="w-full" placeholder="搜索组织…" />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
            <Dropdown>
              <Button aria-label="创建" isIconOnly size="sm" variant="secondary">
                <Plus className="size-4" stroke={1.5} />
              </Button>
              <Dropdown.Popover className="min-w-[180px]" placement="bottom end">
                <Dropdown.Menu>
                  <Dropdown.Item
                    id="create-org"
                    textValue="创建组织"
                    onAction={() => {
                      setOrgDialogMode("create");
                      setEditingOrg(null);
                      setParentOrg(null);
                      setIsOrgDialogOpen(true);
                    }}
                  >
                    <Label className="flex items-center gap-2">
                      <Plus className="size-4" stroke={1.5} />
                      创建组织
                    </Label>
                  </Dropdown.Item>
                  <Dropdown.Item
                    id="add-people"
                    isDisabled={!selectedOrg}
                    textValue="添加人员"
                    onAction={() => setIsPeopleDialogOpen(true)}
                  >
                    <Label className="flex items-center gap-2">
                      <User className="size-4" stroke={1.5} />
                      添加人员
                    </Label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-8 text-sm">
              加载中…
            </div>
          ) : filteredTree.length === 0 ? (
            <div className="text-muted-foreground flex items-center justify-center py-8 text-sm">
              {searchValue.trim() ? "未找到匹配的组织" : "暂无组织数据"}
            </div>
          ) : (
            <FileTree
              aria-label="组织树"
              className="[&_[data-slot=file-tree-icon]]:hidden [&_[data-slot=file-tree-item]]:mb-1"
              defaultExpandedKeys={expandableKeys}
              items={filteredTree}
              selectedKeys={selectedKeys}
              selectionBehavior="replace"
              selectionMode="single"
              showGuideLines="hover"
              size="md"
              onSelectionChange={handleTreeSelection}
            >
              {(item) => renderSidebarItem(item as SidebarTreeItem, treeCtx)}
            </FileTree>
          )}
        </div>
      </div>

      <OrgEditorDialog
        open={isOrgDialogOpen}
        mode={orgDialogMode}
        orgData={editingOrg ?? undefined}
        parentOrg={parentOrg ?? undefined}
        onOpenChange={(open) => {
          setIsOrgDialogOpen(open);
          if (!open) {
            setEditingOrg(null);
            setParentOrg(null);
          }
        }}
        onSuccess={handleOrgCreateSuccess}
      />

      <AlertDialog isOpen={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除组织「{String(orgToDelete?.shortName || orgToDelete?.fullName || "")}」吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsDeleteDialogOpen(false);
                    setOrgToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteOrgMutation.isPending}
                  onPress={handleConfirmDelete}
                >
                  {deleteOrgMutation.isPending ? "删除中…" : "确认删除"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <PeopleEditorDialog
        open={isPeopleDialogOpen}
        mode="create"
        defaultOrgIds={selectedOrg?.id && selectedOrg.id !== SPACE_ROOT_ID ? [selectedOrg.id] : undefined}
        onOpenChange={setIsPeopleDialogOpen}
        onSuccess={() => {}}
      />

      <PeopleSelectDialog
        open={isSetLeaderDialogOpen}
        onOpenChange={(open) => {
          setIsSetLeaderDialogOpen(open);
          if (!open) setOrgForSetLeader(null);
        }}
        title="设置主管"
        headcount={1}
        onConfirm={handleSetLeaderConfirm}
      />
    </>
  );
}
